import { MAX_UPLOAD_BYTES, type CreateUploadResponse, type UploadStatus } from '@hearth/shared';
import type { Api } from '../api';

// Bringing your own world (M10.16): the steps the CLI's `create --upload` takes, in the browser.
// Ask for a signed form, POST the file straight to S3 (with progress), then wait while the
// repack check looks the upload over. Only an accepted upload can start a server.

/** A step that failed, in words to show (never S3's own answer). */
export class UploadError extends Error {}

/** The world check refused the upload: shown with its reason, and the file has to be chosen again. */
export class WorldRefused extends Error {
  constructor(readonly reason: string) {
    super(`Invalid World Upload: ${reason}`);
  }
}

/** The person cancelled the upload: nothing to show. */
export class UploadCancelled extends Error {}

/** Why a file won't do, or '' if it will: a .zip or .tar.gz, not empty, at most 4 GB. */
export function fileProblem(file: { name: string; size: number }): string {
  if (!/\.(zip|tar\.gz|tgz)$/i.test(file.name)) return 'Choose a .zip or .tar.gz file.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_UPLOAD_BYTES) return 'That file is over 4 GB.';
  return '';
}

/** "412 MB of 812 MB · about 3 minutes left": how far the upload has got, once there's enough to tell. */
export function uploadedText(fraction: number, bytes: number, elapsedMs: number): string {
  const done = `${fileSize(bytes * fraction)} of ${fileSize(bytes)}`;
  if (fraction < 0.02 || elapsedMs < 3000) return done;
  const leftMs = (elapsedMs / fraction) * (1 - fraction);
  const minutes = Math.ceil(leftMs / 60_000);
  return `${done} · ${leftMs < 60_000 ? 'less than a minute left' : `about ${minutes} minute${minutes === 1 ? '' : 's'} left`}`;
}

/** "812 MB", "1.2 GB". */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

/**
 * POSTs the signed form to S3: every field, then the file last (S3 ignores fields after it).
 * XMLHttpRequest rather than fetch, for upload progress. S3's error body is never shown: it's
 * S3's own XML, and the form it refused carries temporary credentials.
 */
export function sendForm(form: Pick<CreateUploadResponse, 'url' | 'fields'>, file: Blob, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const body = new FormData();
    for (const [name, value] of Object.entries(form.fields)) body.append(name, value);
    body.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', form.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new UploadError(`The upload didn't go through (${xhr.status}). Try again.`)));
    xhr.onerror = () => reject(new UploadError("The upload didn't go through: the connection dropped. Try again."));
    xhr.onabort = () => reject(new UploadCancelled('Upload cancelled'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(body);
  });
}

/** Waits for the world check's verdict: returns once accepted; throws WorldRefused with its reason. */
export async function waitForCheck(
  api: Pick<Api, 'get'>,
  uploadId: string,
  { sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)), everyMs = 3000, tries = 400 } = {},
): Promise<void> {
  for (let i = 0; i < tries; i++) {
    const status = await api.get<UploadStatus>(`/v1/uploads/${encodeURIComponent(uploadId)}`);
    if (status.status === 'accepted') return;
    if (status.status === 'rejected') throw new WorldRefused(status.reason);
    await sleep(everyMs);
  }
  throw new UploadError('Checking your world is taking too long. Try again later.');
}
